import React, { useEffect, useState } from 'react';
import { Table, DOMHelper, Divider, SortType, Tag, Stack, SelectPicker, Button, IconButton, Whisper, Tooltip, toaster, Message } from 'rsuite';
import { BaseErrorModel, BaseResponseModel, PagedResponseModel, PaginationFilterResult } from '@/models/core.model';
import { AxiosError } from 'axios';
import { toastErrorMessage, toastSuccessMessage } from '@/utils/toasterMessage';
import TablePagination from '@/components/TablePagination';
import { Channel, ChannelPaginationFilterRequest, ChannelKindList, ChannelVisibilityList, ChannelStatusList } from '@/models/channel.model';
import { deleteChannel, getPagedChannels } from '@/services/channel.service';
import { Icon } from '@rsuite/icons';
import { MdDelete } from 'react-icons/md';
import PageContent from '@/components/PageContent';
import { DateTimeFormatOptions } from '@/models/core.model';
import FetchingSelect from '@/components/FetchingSelect';
import { getRunningClubEventsLookup } from '@/services/running-club-event.service';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

export interface RunningClubChannelListProps {
  runningClubId?: number;
  onSelectChannel?: (channel: Channel) => void;
}

const RunningClubChannelList: React.FC<RunningClubChannelListProps> = ({ runningClubId, onSelectChannel }) => {
  const [filterReq, setFilterReq] = useState<ChannelPaginationFilterRequest>(new ChannelPaginationFilterRequest());
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());
  const [items, setItems] = useState<Channel[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData(filterReq);
  }, [filterReq]);

  const fetchData = async (req: ChannelPaginationFilterRequest) => {
    setLoading(true);
    try {
      const finalReq = {
        ...req,
        runningClubId: runningClubId ?? req.runningClubId,
      };
      const result: PagedResponseModel<Channel> = await getPagedChannels(finalReq);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({
          ...pre,
          pageNumber: result.pageNumber,
          totalRecords: result.totalRecords,
          pageSize: result.pageSize
        }));
      }
    } catch (err) {
      setItems([]);
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching Channels:${errorModel?.errorMessage}`);
    }
    finally {
      setLoading(false);
    }
  };

  // Soft delete: the channel disappears for members, its messages stay for moderation.
  const onDeleteItem = (channel: Channel) => {
    const handleYesClick = async () => {
      setLoading(true);
      try {
        const result: BaseResponseModel<null> = await deleteChannel(channel.id);
        if (result.success) {
          toastSuccessMessage(`Channel removed.`);
          fetchData(filterReq);
        }
      } catch (err) {
        const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
        toastErrorMessage(`Error in deleting channel:${errorModel?.errorMessage}`);
      }
      finally {
        setLoading(false);
        toaster.clear();
      }
    };

    toaster.push(
      <Message type="error" closable>
        <p style={{ marginBottom: 10 }}>Remove <strong>{channel.name ?? 'this channel'}</strong>? Members will no longer see it or its messages.</p>
        <div>
          <Button appearance="primary" color='red' onClick={handleYesClick}>Yes</Button>
          <Button appearance="subtle" onClick={() => toaster.clear()} style={{ marginLeft: 10 }}>No</Button>
        </div>
      </Message>,
      { placement: 'topCenter', duration: 0 }
    );
  };

  const handleSortColumn = (sortColumn: string, sortType?: SortType) => {
    setFilterReq(pre => ({
      ...pre,
      orderBy: sortColumn,
      isAscending: sortType == 'asc'
    }));
  };

  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Stack spacing={6}>
          <FetchingSelect
            label="Event"
            fetchData={(text, page) => getRunningClubEventsLookup(text, runningClubId, page)}
            value={filterReq.runningClubEventId}
            onChange={dataKey => setFilterReq(pre => ({...pre, runningClubEventId: dataKey}))}
          />
          <SelectPicker
            data={ChannelKindList}
            placeholder="Kind"
            searchable={false}
            onChange={value => setFilterReq(pre => ({...pre, kind: value ?? undefined}))}
          />
        </Stack>
      </Stack>      
      <Table
        height={Math.max(getHeight(window) - 350, 400)}
        data={items}
        sortColumn={filterReq.orderBy}
        sortType={filterReq.isAscending ? 'asc' : 'desc'}
        onSortColumn={handleSortColumn}
        loading={loading}
        rowHeight={60}
        onRowClick={rowData => onSelectChannel && onSelectChannel(rowData as Channel)}
      >
        <Column flexGrow={1} align="left" verticalAlign="middle" sortable>
          <HeaderCell>Name</HeaderCell>
          <Cell dataKey="name" />
        </Column>
        <Column flexGrow={1} align="center" verticalAlign="middle">
          <HeaderCell>Kind</HeaderCell>
          <Cell>
            {(rowData: Channel) => ChannelKindList.find(k => k.value === rowData.kind)?.label ?? rowData.kind}
          </Cell>
        </Column>
        <Column flexGrow={1} align="left" verticalAlign="middle">
          <HeaderCell>Event</HeaderCell>
          <Cell>
            {(rowData: Channel) => rowData.runningClubEvent?.name ?? '—'}
          </Cell>
        </Column>
        <Column flexGrow={1} align="center" verticalAlign="middle">
          <HeaderCell>Visibility</HeaderCell>
          <Cell>
            {(rowData: Channel) => ChannelVisibilityList.find(v => v.value === rowData.visibility)?.label ?? rowData.visibility}
          </Cell>
        </Column>
        <Column flexGrow={1} align="center" verticalAlign="middle">
          <HeaderCell>Status</HeaderCell>
          <Cell>
            {(rowData: Channel) => (
              <Tag color={rowData.status === 1 ? 'green' : 'orange'}>
                {ChannelStatusList.find(s => s.value === rowData.status)?.label ?? rowData.status}
              </Tag>
            )}
          </Cell>
        </Column>
        <Column flexGrow={1} align="center" verticalAlign="middle" sortable>
          <HeaderCell>Messages</HeaderCell>
          <Cell dataKey="messageCount" />
        </Column>
        <Column flexGrow={1} align="left" verticalAlign="middle" sortable>
          <HeaderCell>Last Activity</HeaderCell>
          <Cell>
            {(rowData: Channel) => rowData.lastMessageAt ? rowData.lastMessageAt.toLocaleString(undefined, DateTimeFormatOptions) : '—'}
          </Cell>
        </Column>
        <Column width={70} align="left" verticalAlign="middle" fixed="right">
          <HeaderCell>...</HeaderCell>
          <Cell style={{ padding: '6px' }}>
            {(rowData: Channel) => (
              <Stack spacing={8} onClick={e => e.stopPropagation()}>
                <Whisper placement="top" trigger="hover" speaker={<Tooltip>Delete</Tooltip>}>
                  <IconButton circle size="sm" color="red" appearance="ghost"
                    onClick={() => onDeleteItem(rowData)}
                    icon={<Icon as={MdDelete} />} />
                </Whisper>
              </Stack>
            )}
          </Cell>
        </Column>
      </Table>
      <Divider />
      <TablePagination
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={dataKey => setFilterReq(pre => ({ ...pre, pageNumber: dataKey }))}
        onChangeLimit={dataKey => setFilterReq(pre => ({ ...pre, pageSize: dataKey }))}
      />
    </PageContent>
  );
};

export default RunningClubChannelList;
